import { Schema } from '../schema.js';
import { ExternalFieldData } from '../DataSetService/yaml-models.js';
import { PModel, PropertyPath } from '../engine.js';
import { BigNumber, getTypeRef, ObjectType, PropertyPathWalker } from '../index.js';
import { MLString } from '../types.js';

/***
 * Registry holding all external fields data and allowing to work with such information in a structured way.
 */
export class ExternalFieldRegistry {
  private properties = new Map<string, object[]>();

  constructor(private schema: Schema) {
  }

  addExternalField(externalField: ExternalFieldData) {
    const key = this.externalFieldKey(externalField);
    if (this.properties.has(key)) {
      throw new Error(`${key} externalField already declared`);
    }
    const propertyRecords = this.createRecords(externalField);
    this.properties.set(key, propertyRecords);
  }

  getExternalFieldValue<T>(model: PModel, path: PropertyPath): T {
    const typeRef = getTypeRef(model);
    const type = this.schema.getType(typeRef);
    if (type instanceof ObjectType) {
      const externalFieldData = type.externals.find(external => external.name === path);
      const defaultValue = externalFieldData.default;
      const key = this.externalFieldKey({ name: externalFieldData.name, type: typeRef });
      const records = this.properties.get(key);
      if (!records) {
        return defaultValue;
      }

      const modelValue: { [k: string]: unknown } = {};
      externalFieldData.properties.forEach(property => {
        const externalField = type.externals.find(external => external.name === property);
        if (externalField) {
          // TODO: Add cyclic recursion check
          modelValue[property] = this.getExternalFieldValue(model, property);
        } else {
          const value = new PropertyPathWalker(property, this.schema, model, typeRef)
            .getPropertyValue()
            .value
          modelValue[property] = value;
        }
      })

      const externalFieldName = externalFieldData.name;
      const result = records.find(record => {
        for (let [key, value] of Object.entries(record)) {
          if (key == externalFieldName) {
            continue;
          }
          const keyValue = modelValue[key];
          //Special case. Undefined cannot be matched with any value
          if (keyValue === undefined) {
            return false;
          }
          if (value instanceof MLString) {
            if (keyValue instanceof MLString) {
              if (!value.eq(keyValue)) {
                return null;
              }
            } else {
              throw new TypeError(`Invalid type for ${keyValue}. MLString expected`);
            }
          } else if (value instanceof BigNumber) {
            if (keyValue instanceof BigNumber) {
              if (!value.eq(keyValue)) {
                return null;
              }
            } else {
              throw new TypeError(`Invalid type for ${keyValue}. BigNumber expected`);
            }
          } else {
            if (modelValue[key] !== value) {
              return false;
            }
          }
        }
        return true;
      })
      if (result == undefined) {
        console.warn(`ExternalField data for ${path} of ${typeRef} not found. ModelValue = ${JSON.stringify(modelValue)}`)
        return defaultValue;
      }
      return result[externalFieldData.name];

    } else {
      throw new Error('Invalid model');
    }
  }

  private createRecords(externalField: ExternalFieldData): object[] {
    const result = [];
    for (let pos = 0; pos < externalField.data.length; pos++) {
      let obj = {};
      for (let i = 0; i < externalField.order.length; i++) {
        const fieldName = externalField.order[i];
        const value = externalField.data[pos][i];
        obj[fieldName] = value;
      }
      result.push(obj);
    }
    return result;
  }

  private externalFieldKey(externalField: { name: string, type: string }) {
    return externalField.type + '/' + externalField.name;
  }
}
